"use client";
import Link from "next/link";
import { useEffect, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  Circle,
  Clock3,
  Crosshair,
  ExternalLink,
  FileUp,
  Flag,
  Lightbulb,
  Loader2,
  LockKeyhole,
  Monitor,
  MonitorSmartphone,
  Network,
  Pause,
  Printer,
  Radio,
  StickyNote,
  Play,
  RefreshCw,
  Send,
  Terminal,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { api, post } from "@/lib/api/client";
import { clock, safeWorkspaceUrl } from "@/lib/format";
import { KZ_REFS, KZ_SHORT, MITRE_NAMES } from "@/lib/range/mappings";
import type {
  PracticeResult,
  Session,
  StartSessionResponse,
  Task,
  TaskSubmission,
} from "@/lib/types";
import { RequireAuth } from "@/components/auth";
import {
  ActionButton,
  Badge,
  Button,
  DataState,
  Empty,
  ErrorState,
  Modal,
  PageHeading,
  Progress,
  Stat,
} from "@/components/ui";
// OS таңдау: Kali / Parrot / Ubuntu (жаңа lab кезінде қолданылады)
const OS_LABELS: Record<string, string> = {
  kali: "Kali Linux",
  parrot: "Parrot OS",
  ubuntu: "Ubuntu 24.04",
};

function OsPicker({
  running,
  currentOs,
  labId,
  scenarioId,
  sessionKey,
  onChanged,
}: {
  running: boolean;
  currentOs?: string;
  labId?: string;
  scenarioId?: string;
  sessionKey?: string;
  onChanged: () => void;
}) {
  const router = useRouter();
  const [os, setOs] = useState(currentOs ?? "kali");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setOs(currentOs ?? "kali"), [currentOs]);
  // OS ауыстыру: қазіргі lab тоқтатылып, жаңа lab таңдалған OS-пен ашылады
  // (run.os бекітілген — ескі run-ды start қылса да ескі OS-ті қайтарады)
  async function pick(v: string) {
    setOs(v);
    if (v === (currentOs ?? "kali")) return;
    const switchNow =
      Boolean(labId) &&
      window.confirm(
        "OS ауыстыру: қазіргі лаборатория жабылып, жаңа лаборатория жаңа OS-пен іске қосылады. Жалғастырамыз ба?",
      );
    if (Boolean(labId) && !switchNow) {
      setOs(currentOs ?? "kali");
      return;
    }
    setBusy(true);
    try {
      await post("/users/me/lab-os", { os: v });
      if (labId && sessionKey && scenarioId) {
        // 1) ескі сессия/lab жабу (destroy)
        await post(`/sessions/${sessionKey}/finish`).catch(() => undefined);
        // 2) жаңа сессия (жаңа OS-пен)
        const res = await post<{ session_id: string }>("/sessions", {
          scenario_id: scenarioId,
        });
        router.push(
          `/practice/${encodeURIComponent(res.session_id)}/workspace`,
        );
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  return (
    <label
      className="os-picker"
      title={
        running
          ? "OS ауыстыру: жаңа лаборатория іске қосылады"
          : "Таңдалған OS жаңа лабораторияда қолданылады"
      }
    >
      <MonitorSmartphone size={13} />
      <select
        value={os}
        disabled={busy}
        onChange={(e) => pick(e.target.value)}
        aria-label="Лаборатория OS таңдау"
      >
        <option value="kali">Kali Linux</option>
        <option value="parrot">Parrot OS</option>
        <option value="ubuntu">Ubuntu 24.04</option>
      </select>
      {saved && <Check size={12} className="os-saved" />}
      {busy && <Loader2 size={12} className="spin" />}
    </label>
  );
}

function Timer({ session }: { session: Session }) {
  const t = useT();
  const [now, setNow] = useState<number>();
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds =
    now === undefined
      ? undefined
      : session.expires_at
        ? (Date.parse(session.expires_at) - now) / 1000
        : (now - Date.parse(session.started_at)) / 1000;
  return (
    <span className="timer">
      <Clock3 size={18} />
      {seconds === undefined || !Number.isFinite(seconds)
        ? "—:—"
        : clock(seconds)}
      <small>{session.expires_at ? t("ws.remaining") : t("ws.in_practice")}</small>
    </span>
  );
}
/** PHASE 4: Worked Example — практика бұрын әдісті үйрететін мысал
 *  (басқа синтетикалық нысанада; ашылуы track-талады, бірақ жаза қоймайды). */
function WorkedExampleBlock({
  example,
  storageKey,
}: {
  example: import("@/lib/types").WorkedExample;
  storageKey: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem("cyberalem.we." + storageKey) === "1";
    } catch {
      return false;
    }
  });
  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        localStorage.setItem("cyberalem.we." + storageKey, "1");
      } catch {
        /* ignore */
      }
    }
  }
  const row = (label: string, children: React.ReactNode) => (
    <div className="we-row">
      <span className="we-label">{label}</span>
      {children}
    </div>
  );
  return (
    <div className={`task-section worked-example ${open ? "open" : ""}`}>
      <div className="between" style={{ flexWrap: "wrap", gap: 8 }}>
        <button type="button" className="button secondary" onClick={toggle}>
          {open ? (
            t("ws.example_hide")
          ) : open === false && localStorage.getItem("cyberalem.we." + storageKey) === "1" ? (
            t("ws.example_seen")
          ) : (
            t("ws.example_view")
          )}
        </button>
        {open && (
          <span className="muted small">{t("ws.example_to_practice")} ↓</span>
        )}
      </div>
      {open && (
        <div className="we-body">
          <p className="we-note">{t("ws.example_note")}</p>
          {row(t("ws.example_situation"), <p>{example.situation}</p>)}
          {row(
            t("ws.example_observe"),
            <ul className="we-list">
              {example.observations.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>,
          )}
          {row(
            t("ws.example_investigate"),
            <ul className="we-list">
              {example.investigate.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>,
          )}
          {row(t("ws.example_reasoning"), <p>{example.reasoning}</p>)}
          {row(
            t("ws.example_tools"),
            <div className="we-tools">
              {example.tool_usage.map((tool, i) => (
                <div key={i} className="we-tool">
                  <code>{tool.cmd}</code>
                  <span className="muted small">{tool.note}</span>
                </div>
              ))}
            </div>,
          )}
          {row(t("ws.example_interpret"), <p>{example.interpretation}</p>)}
        </div>
      )}
    </div>
  );
}

function TaskCard({
  task,
  sessionId,
  onUpdate,
}: {
  task: Task;
  sessionId: string;
  onUpdate: () => void;
}) {
  const t = useT();
  const [answer, setAnswer] = useState("");
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string>();
  const [error, setError] = useState<string>();
  const [hint, setHint] = useState<string>();
  const supported = ["text", "choice", "file", "command", "artifact"].includes(
    task.type,
  );
  const disabled = ["LOCKED", "COMPLETED", "PASSED", "PENDING"].includes(
    task.status,
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      let payload: FormData | { answer: string };
      if (["file", "artifact"].includes(task.type)) {
        if (!file) throw new Error(t("ws.pick_file"));
        payload = new FormData();
        payload.append("evidence", file);
      } else {
        if (!answer.trim()) throw new Error(t("ws.enter_answer"));
        payload = { answer };
      }
      const result = await post<TaskSubmission>(
        `/sessions/${encodeURIComponent(sessionId)}/tasks/${encodeURIComponent(task.id)}/submit`,
        payload,
      );
      setFeedback(result.feedback);
      onUpdate();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="task-content">
      <div className="between">
        <span className="eyebrow">{t("ws.current_task")}</span>
        <Badge value={task.status} />
      </div>
      <h2>{task.title}</h2>
      <p className="preserve-lines">{task.description}</p>
      {task.goal && (
        <div className="task-section">
          <h3>{t("ws.goal")}</h3>
          <p>{task.goal}</p>
        </div>
      )}
      {task.worked_example && (
        <WorkedExampleBlock
          example={task.worked_example}
          storageKey={`${sessionId}:${task.id}`}
        />
      )}
      <div className="task-section">
        <h3>{t("ws.instruction")}</h3>
        <ol className="plain-list">
          {task.instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>
      {task.actions && task.actions.length > 0 && (
        <div className="task-section actions-requirement">
          <h3>🎯 Обязательные действия в терминале Kali</h3>
          <p className="muted">
            За полный балл эти действия должны быть выполнены в терминале
            до отправки ответа (без них — 50% веса задания).
          </p>
          <ul className="plain-list">
            {task.actions.map((a, i) => (
              <li key={i}>{a.label}</li>
            ))}
          </ul>
        </div>
      )}
      {task.mapping &&
        (task.mapping.mitre?.length ?? 0) + (task.mapping.kz?.length ?? 0) >
          0 && (
          <div className="task-section mapping-section">
            <h3>🛡 Соответствие стандартам</h3>
            <div className="mapping-chips">
              {(task.mapping.mitre ?? []).map((id) => (
                <span key={id} className="chip chip-mitre" title={id}>
                  {MITRE_NAMES[id] ?? id}
                </span>
              ))}
              {(task.mapping.kz ?? []).map((id) => (
                <span key={id} className="chip chip-kz" title={KZ_REFS[id]}>
                  {KZ_SHORT[id] ?? id}
                </span>
              ))}
            </div>
          </div>
        )}
      <form onSubmit={submit} className="task-form">
        {supported ? (
          <>
            <label className="field">
              <span>
                {task.type === "command"
                  ? "Команда"
                  : ["file", "artifact"].includes(task.type)
                    ? "Файл / evidence"
                    : "Ваш ответ"}
              </span>
              {["text", "command"].includes(task.type) && (
                <textarea
                  className={task.type === "command" ? "mono" : ""}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={3}
                  required
                  disabled={disabled || busy}
                  placeholder={
                    task.type === "command"
                      ? "Введите команду для проверки…"
                      : "Введите ответ…"
                  }
                />
              )}
              {["file", "artifact"].includes(task.type) && (
                <span className="upload-control">
                  <FileUp size={21} />
                  <input
                    type="file"
                    accept={typeof task.accept === "string" ? task.accept : undefined}
                    required
                    disabled={disabled || busy}
                    onChange={(e) => setFile(e.target.files?.[0])}
                  />
                </span>
              )}
              {task.type === "choice" && (
                <select
                  required
                  value={answer}
                  disabled={disabled || busy}
                  onChange={(e) => setAnswer(e.target.value)}
                >
                  <option value="">{t("ws.pick_option")}</option>
                  {task.options?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <Button type="submit" busy={busy} disabled={disabled}>
              <Send size={16} />
              {t("ws.submit_answer")}
            </Button>
          </>
        ) : (
          <div className="notice">
            {t("ws.unsupported", { type: task.type })}
          </div>
        )}
        {(feedback || task.feedback) && (
          <div className="notice" role="status">
            {feedback ?? task.feedback}
          </div>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </form>
      {task.hint_available && !disabled && (
        <div className="hint-section">
          <ActionButton
            variant="secondary"
            action={async () => {
              const result = await post<{ text: string }>(
                `/sessions/${encodeURIComponent(sessionId)}/tasks/${encodeURIComponent(task.id)}/hint`,
              );
              setHint(result.text);
            }}
          >
            <Lightbulb size={16} />
            {t("ws.get_hint")}
          </ActionButton>
          <small className="muted">
            {t("ws.hint_note")}
          </small>
          {hint && (
            <p className="notice" role="status">
              {hint}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
function WorkspaceContent({ id }: { id: string }) {
  const t = useT();
  const session = useApi<Session>(`/sessions/${encodeURIComponent(id)}`, 5000);
  const tasks = useApi<Task[]>(
    `/sessions/${encodeURIComponent(id)}/tasks`,
    5000,
  );
  const [selected, setSelected] = useState<string>();
  const [finish, setFinish] = useState(false);
  const [frameFailed, setFrameFailed] = useState(false);
  const [labView, setLabView] = useState<"desktop" | "terminal" | "topology" | "notes">("desktop");
  const router = useRouter();
  // QUIZ-сессии не имеют лабы вообще — показываем заметки, а не «лаба готовится»
  const isNoLab = session.data?.has_lab === false;
  useEffect(() => {
    if (isNoLab) setLabView("notes");
  }, [isNoLab]);
  const current =
    tasks.data?.find((t) => t.id === selected) ??
    tasks.data?.find((t) => t.id === session.data?.current_task_id) ??
    tasks.data?.find(
      (t) => !["LOCKED", "COMPLETED", "PASSED"].includes(t.status),
    ) ??
    tasks.data?.[0];
  const workspaceUrl = safeWorkspaceUrl(
    session.data?.lab?.workspace_url,
    process.env.NEXT_PUBLIC_WORKSPACE_ORIGINS ?? "",
    typeof window !== "undefined" ? window.location.hostname : undefined,
  );
  const terminalUrl = safeWorkspaceUrl(
    session.data?.lab?.terminal_url,
    process.env.NEXT_PUBLIC_WORKSPACE_ORIGINS ?? "",
    typeof window !== "undefined" ? window.location.hostname : undefined,
  );
  const hasTerminal = !!terminalUrl;
  const activeFrameUrl =
    (labView === "terminal" && hasTerminal ? terminalUrl : workspaceUrl) ??
    undefined;
  return (
    <DataState
      loading={!session.data && session.loading}
      error={!session.data ? session.error : undefined}
      retry={session.reload}
    >
      {session.data && (
        <>
          <div className="workspace-heading">
            <div>
              <Link
                href={`/practices/${encodeURIComponent(session.data.scenario_id)}`}
                className="back-link"
              >
                <ArrowLeft size={15} />
                {t("ws.practice_desc")}
              </Link>
              <h1>{session.data.scenario_title}</h1>
            </div>
            <Timer session={session.data} />
          </div>
          {(() => {
            const lab = session.data.lab;
            if (!lab || lab.status !== "RUNNING") return null;
            const left = lab.remaining_minutes;
            if (left == null || left > 30) return null;
            return (
              <div className="ttl-warning">
                <Clock3 size={16} />
                <span>
                  {t("ws.ttl_warning", { min: left })}
                </span>
              </div>
            );
          })()}
          {session.data.lab &&
            ["RUNNING", "PAUSED"].includes(session.data.lab.status) && (
              <div className="workspace-lab-actions">
                {session.data.lab.status === "RUNNING" ? (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      await post(`/labs/${session.data!.lab!.id}/pause`);
                      session.reload();
                    }}
                  >
                    <Pause size={15} />
                    {t("ws.pause_lab")}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={async () => {
                      await post(`/labs/${session.data!.lab!.id}/resume`);
                      session.reload();
                    }}
                  >
                    <Play size={15} />
                    {t("ws.resume_lab")}
                  </Button>
                )}
                {session.data.lab.status === "PAUSED" && (
                  <span className="lab-paused-hint">{t("ws.lab_paused_hint")}</span>
                )}
              </div>
            )}
          {session.error && (
            <ErrorState error={session.error} retry={session.reload} />
          )}
          {["COMPLETED", "FAILED"].includes(session.data.status) ? (
            <Empty
              title={t("ws.finished_title")}
              action={
                <Link
                  href={`/practice/${encodeURIComponent(id)}/result`}
                  className="button primary"
                >
                  {t("ws.view_result")}
                </Link>
              }
            />
          ) : (
            <>
              <div className="workspace-grid">
                <aside className="task-sidebar">
                  <div className="task-sidebar-heading">
                    <h2>{t("ws.tasks")}</h2>
                    <Progress value={session.data.progress} label={t("ws.done")} />
                  </div>
                  <DataState
                    loading={!tasks.data && tasks.loading}
                    error={!tasks.data ? tasks.error : undefined}
                    retry={tasks.reload}
                  >
                    {tasks.data?.length ? (
                      <nav aria-label={t("ws.tasks_nav")}>
                        {tasks.data.map((task, index) => (
                          <button
                            key={task.id}
                            className={`task-nav-item ${current?.id === task.id ? "selected" : ""}`}
                            onClick={() => setSelected(task.id)}
                            disabled={task.status === "LOCKED"}
                          >
                            {["COMPLETED", "PASSED"].includes(task.status) ? (
                              <Check size={17} />
                            ) : task.status === "LOCKED" ? (
                              <LockKeyhole size={17} />
                            ) : (
                              <Circle size={17} />
                            )}
                            <span>
                              <small>{t("ws.task_n", { n: index + 1 })}</small>
                              {task.title}
                            </span>
                          </button>
                        ))}
                      </nav>
                    ) : (
                      <Empty
                        title={t("ws.tasks_preparing")}
                        text={t("ws.tasks_preparing_text")}
                      />
                    )}
                  </DataState>
                </aside>
                <div className="task-main">
                  {tasks.error && tasks.data && (
                    <ErrorState error={tasks.error} retry={tasks.reload} />
                  )}
                  {current && (
                    <TaskCard
                      key={current.id}
                      task={current}
                      sessionId={id}
                      onUpdate={() => {
                        session.reload();
                        tasks.reload();
                      }}
                    />
                  )}
                </div>
                <section className="terminal-panel">
                  <div className="terminal-header">
                    <span>
                      <Terminal size={18} />
                      {t("ws.work_env")}
                    </span>
                    {session.data.has_lab !== false && (
                      <OsPicker
                        running={
                          ["RUNNING", "READY"].includes(session.data.lab?.status ?? "")
                        }
                        currentOs={session.data.lab?.os}
                        labId={session.data.lab?.id}
                        scenarioId={session.data.scenario_id}
                        sessionKey={session.data.id}
                        onChanged={session.reload}
                      />
                    )}
                    <span className="terminal-tabs">
                      {hasTerminal && (
                        <>
                          <Button
                            variant={labView === "desktop" ? "primary" : "ghost"}
                            onClick={() => setLabView("desktop")}
                          >
                            {t("ws.desktop")}
                          </Button>
                          <Button
                            variant={labView === "terminal" ? "primary" : "ghost"}
                            onClick={() => setLabView("terminal")}
                          >
                            {t("ws.terminal")}
                          </Button>
                        </>
                      )}
                      <Button
                        variant={labView === "topology" ? "primary" : "ghost"}
                        onClick={() => setLabView("topology")}
                      >
                        <Network size={15} /> {t("ws.topology")}
                      </Button>
                      <Button
                        variant={labView === "notes" ? "primary" : "ghost"}
                        onClick={() => setLabView("notes")}
                      >
                        <StickyNote size={15} /> {t("ws.notes")}
                      </Button>
                      {(labView === "terminal" ? terminalUrl : workspaceUrl) && (
                        <a
                          href={(labView === "terminal" ? terminalUrl : workspaceUrl) ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t("ws.open_new_tab")}
                        >
                          <ExternalLink size={17} />
                        </a>
                      )}
                    </span>
                  </div>
                  {labView === "topology" ? (
                    <div className="lab-topology">
                      <p className="muted small">{t("ws.topology_desc")}</p>
                      {session.data.lab?.machines?.length ? (
                        <div className="topology-nodes">
                          {session.data.lab.machines.map((m) => (
                            <div key={m.name} className={`topology-node role-${m.role}`}>
                              <span className="topology-icon">
                                {m.role === "workstation" ? <Monitor size={18} /> : m.role === "target" ? <Crosshair size={18} /> : <Radio size={18} />}
                              </span>
                              <div>
                                <strong>{m.name}</strong>
                                <span className="topology-ip">{m.ip ?? "…"}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="muted">{t("ws.topology_none")}</p>
                      )}
                    </div>
                  ) : labView === "notes" ? (
                    <SessionNotesPanel sessionId={encodeURIComponent(id)} />
                  ) : activeFrameUrl &&
                  ["READY", "RUNNING"].includes(
                    session.data.lab?.status ?? "",
                  ) &&
                  !frameFailed ? (
                    <iframe
                      title={t("ws.lab_title")}
                      src={activeFrameUrl}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
                      allow="fullscreen"
                      referrerPolicy="no-referrer"
                      onError={() => setFrameFailed(true)}
                    />
                  ) : (
                    <div className="terminal-empty">
                      <Terminal size={38} strokeWidth={1.3} />
                      <h3>
                        {!session.data.lab && isNoLab
                          ? t("ws.no_lab_heading")
                          : session.data.lab?.status === "CREATING"
                            ? t("ws.lab_creating")
                            : session.data.lab?.status === "ERROR"
                              ? t("ws.lab_unavailable")
                              : session.data.lab?.status === "STOPPED"
                                ? t("ws.lab_stopped")
                                : frameFailed
                                  ? t("ws.lab_frame_failed")
                                  : t("ws.work_env")}
                      </h3>
                      <p>
                        {!session.data.lab && isNoLab
                          ? t("ws.no_lab_text")
                          : session.data.lab?.status === "STOPPED"
                            ? t("ws.lab_stopped_hint")
                            : session.data.lab?.error ??
                            (session.data.lab?.workspace_url && !workspaceUrl
                              ? t("ws.lab_not_allowed")
                              : t("ws.lab_waiting"))}
                      </p>
                      {frameFailed && (
                        <Button
                          variant="secondary"
                          onClick={() => setFrameFailed(false)}
                        >
                          {t("ws.lab_retry")}
                        </Button>
                      )}
                      {/* Студент өз Kali-сін ӨЗІ қайта іске қоса алады (админсіз) */}
                      {session.data.lab &&
                        ["STOPPED", "ERROR"].includes(session.data.lab.status) && (
                          <ActionButton
                            variant="primary"
                            action={async () => {
                              await post(`/labs/${session.data!.lab!.id}/start`);
                              session.reload();
                            }}
                          >
                            <Play size={16} />
                            {t("ws.start_lab")}
                          </ActionButton>
                        )}
                      {session.data.lab?.status === "DESTROYED" && (
                        <ActionButton
                          variant="primary"
                          action={async () => {
                            const res = await post<{ session_id: string }>(
                              "/sessions",
                              { scenario_id: session.data!.scenario_id },
                            );
                            router.push(
                              `/practice/${encodeURIComponent(res.session_id)}/workspace`,
                            );
                          }}
                        >
                          <Play size={16} />
                          {t("ws.new_session")}
                        </ActionButton>
                      )}
                    </div>
                  )}
                </section>
              </div>
              <div className="workspace-footer">
                <span className="inline">
                  <Terminal size={17} />
                  {t("ws.lab")}{" "}
                  {session.data.lab ? (
                    <Badge value={session.data.lab.status} />
                  ) : (
                    t("ws.not_assigned")
                  )}
                </span>
                <div className="inline">
                  {session.data.lab &&
                    ["RUNNING", "ERROR", "STOPPED"].includes(
                      session.data.lab.status,
                    ) && (
                      <ActionButton
                        variant="secondary"
                        action={async () => {
                          await post(`/labs/${session.data!.lab!.id}/reset`);
                          session.reload();
                        }}
                      >
                        <RefreshCw size={16} />
                        {t("ws.reset_lab")}
                      </ActionButton>
                    )}
                  <Button variant="secondary" onClick={() => setFinish(true)}>
                    <Flag size={16} />
                    Завершить практику
                  </Button>
                </div>
              </div>
            </>
          )}
          <Modal
            title="Завершить практику?"
            open={finish}
            onClose={() => setFinish(false)}
          >
            <p>
              Сервер сохранит выполненные задания и рассчитает итоговый
              результат. Рабочая лаборатория может быть остановлена.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setFinish(false)}>
                Продолжить работу
              </Button>
              <ActionButton
                action={async () => {
                  await post(`/sessions/${encodeURIComponent(id)}/finish`);
                  router.push(`/practice/${encodeURIComponent(id)}/result`);
                }}
              >
                Завершить
              </ActionButton>
            </div>
          </Modal>
        </>
      )}
    </DataState>
  );
}
/** PHASE 5: Notes — студенттің зертхана ішіндегі жазбалары (автосақтау). */
function SessionNotesPanel({ sessionId }: { sessionId: string }) {
  const t = useT();
  const [content, setContent] = useState("");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api<{ content: string; updated_at: string | null }>(
      `/sessions/${sessionId}/notes`,
    )
      .then((d) => {
        setContent(d.content);
        setSavedAt(d.updated_at);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [sessionId]);

  // автосақтау: 1.2s debounce
  useEffect(() => {
    if (!loaded) return;
    setSaving(true);
    const h = window.setTimeout(() => {
      api(`/sessions/${sessionId}/notes`, {
        method: "PUT",
        body: JSON.stringify({ content }),
      })
        .then((d) => setSavedAt((d as { updated_at: string }).updated_at))
        .catch(() => undefined)
        .finally(() => setSaving(false));
    }, 1200);
    return () => window.clearTimeout(h);
  }, [content, loaded, sessionId]);

  return (
    <div className="lab-notes">
      <div className="between" style={{ marginBottom: 8 }}>
        <span className="muted small">{t("ws.notes_hint")}</span>
        <span className={`notes-saved ${saving ? "saving" : ""}`}>
          {saving ? t("ws.notes_saving") : t("ws.notes_saved")}
        </span>
      </div>
      <textarea
        className="input notes-textarea"
        rows={18}
        value={content}
        placeholder={t("ws.notes_ph")}
        onChange={(e) => setContent(e.target.value)}
      />
    </div>
  );
}

export function WorkspacePage({ id }: { id: string }) {
  return (
    <RequireAuth>
      <WorkspaceContent id={id} />
    </RequireAuth>
  );
}
function ResultContent({ id }: { id: string }) {
  const request = useApi<PracticeResult>(
    `/sessions/${encodeURIComponent(id)}/result`,
  );
  const router = useRouter();
  return (
    <DataState {...request} retry={request.reload}>
      {request.data && (
        <>
          <PageHeading
            eyebrow="РЕЗУЛЬТАТ ПРАКТИКИ"
            title={request.data.scenario_title}
            action={<Badge value={request.data.status} />}
          />
          <section className="result-hero">
            <div
              className="score-ring"
              style={
                {
                  "--score": `${Math.max(0, Math.min(100, request.data.completion))}%`,
                } as React.CSSProperties
              }
            >
              <div>
                <strong>{request.data.score}</strong>
                <span>из {request.data.max_score} баллов</span>
              </div>
            </div>
            <div>
              <h2>Практика завершена</h2>
              <p>
                Результат сохранён. Посмотрите обратную связь
                <br />и рекомендации перед следующим шагом.
              </p>
              <Progress
                value={request.data.completion}
                label="Выполнение заданий"
              />
            </div>
          </section>
          {request.data.certificate && (
            <section className="panel cert-granted">
              <div className="cert-granted-icon" aria-hidden="true">
                <BadgeCheck size={26} />
              </div>
              <div className="cert-granted-body">
                <h2>Аттестация пройдена — сертификат выдан</h2>
                <p>
                  Номер: <code>{request.data.certificate.id}</code>. Сохраните или
                  распечатайте — верификация работает публично по ссылке.
                </p>
              </div>
              <div className="cert-granted-actions">
                <Link className="button primary" href={request.data.certificate.url}>
                  Открыть сертификат
                  <ArrowRight size={16} />
                </Link>
                <button
                  className="button secondary"
                  onClick={() =>
                    window.open(request.data!.certificate!.url, "_blank")
                  }
                >
                  <Printer size={16} />
                  Печать
                </button>
              </div>
            </section>
          )}
          {request.data.ai_report && (
            <section className="panel ai-report">
              <div className="ai-report-score">
                <span className="num">{request.data.ai_report.score}</span>
                <span>/100</span>
              </div>
              <div className="ai-report-body">
                <h2>ИИ-оценка отчёта (CyberScore)</h2>
                <p>
                  Отчёт «{request.data.ai_report.task_title}» автоматически
                  оценён движком {request.data.ai_report.engine}: факты, структура,
                  правовая рамка, ясность. Разбор по критериям и история — на
                  странице CyberScore.
                </p>
              </div>
              <Link className="button secondary" href="/cyberscore">
                CyberScore
                <ArrowRight size={16} />
              </Link>
            </section>
          )}
          <div className="stats-grid">
            <Stat label="Время" value={clock(request.data.duration_seconds)} />
            <Stat label="Попытки" value={request.data.attempts} />
            <Stat label="Ошибки" value={request.data.errors} />
            <Stat label="Подсказки" value={request.data.hints} />
            {request.data.audit && (
              <>
                <Stat label="Команды в Kali" value={request.data.audit.commands} />
                <Stat
                  label="Действия подтверждены"
                  value={`${request.data.audit.actions_verified}/${request.data.audit.tasks_with_actions}`}
                />
              </>
            )}
          </div>
          <div className="two-columns">
            <section className="panel">
              <h2>Результаты заданий</h2>
              <div className="stack">
                {request.data.tasks.map((task) => (
                  <div key={task.id} className="result-task">
                    <div className="between">
                      <strong>{task.title}</strong>
                      <Badge value={task.status} />
                    </div>
                    {task.actions && task.actions.length > 0 && (
                      <div className="stack small-stack">
                        <p className={task.evidence === "full" ? "result-evidence ok" : "result-evidence partial"}>
                          {task.evidence === "full"
                            ? `✓ Действия в терминале подтверждены (${task.actions.length}/${task.actions.length}) — полный балл`
                            : `⚠ Действия не подтверждены (${task.actions.filter((a) => a.found).length}/${task.actions.length}) — начислено 50% веса`}
                        </p>
                        <ul className="plain-list muted">
                          {task.actions.map((a, i) => (
                            <li key={i}>
                              {a.found ? "✓" : "✗"} {a.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {task.feedback && <p className="muted">{task.feedback}</p>}
                  </div>
                ))}
              </div>
            </section>
            <section className="panel">
              <h2>Рекомендации</h2>
              {request.data.recommendations.length ? (
                <ul className="plain-list">
                  {request.data.recommendations.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Дополнительных рекомендаций нет.</p>
              )}
            </section>
          </div>
          <div className="inline section">
            <ActionButton
              action={async () => {
                const result = await post<StartSessionResponse>("/sessions", {
                  scenario_id: request.data!.scenario_id,
                });
                router.push(
                  `/practice/${encodeURIComponent(result.session_id)}/workspace`,
                );
              }}
            >
              Пройти ещё раз
            </ActionButton>
            <Link href="/practices" className="button secondary">
              Вернуться в каталог
              <ArrowRight size={16} />
            </Link>
          </div>
        </>
      )}
    </DataState>
  );
}
export function ResultPage({ id }: { id: string }) {
  return (
    <RequireAuth>
      <ResultContent id={id} />
    </RequireAuth>
  );
}
